/**
 * Main Layout Component
 * Layout for authenticated pages with sidebar and navbar
 */

import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Drawer, AppBar, Toolbar, IconButton, Typography, Avatar, Menu, MenuItem, Divider } from '@mui/material';
import { Menu as MenuIcon, MoreVert } from '@mui/icons-material';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import IdleTimeoutHandler from '../components/IdleTimeoutHandler';
import NotificationBell from '../../features/demands/components/NotificationBell';
import settingsService from '../../features/settings/services/settingsService';
import { IMG_BASE_URL } from '../utils/constants';
import logger from '../utils/logger';
import defaultLogo from '../../assets/Final-logo.png';

const DRAWER_WIDTH_MINIMIZED = 70;
const DRAWER_WIDTH = 260;

const MainLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [logoImage, setLogoImage] = useState('');
  const profileMenuAnchorRef = useRef(null);

  const handleProfileMenuOpen = () => {
    setProfileMenuOpen(true);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuOpen(false);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const handleChangePassword = () => {
    handleProfileMenuClose();
    navigate('/change-password');
  };
  const toggleSidebar = () => {
    setSidebarMinimized(!sidebarMinimized);
  };
 
  const handleSidebarMouseEnter = () => {
    if (sidebarMinimized) {
      setSidebarHovered(true);
    }
  };
 
  const handleSidebarMouseLeave = () => {
    setSidebarHovered(false);
  };
 
  const isSidebarExpanded = !sidebarMinimized || sidebarHovered;
 
  // Load logo from backend
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await settingsService.getImageDetails();
        if (response && response.length > 0) {
          // ImageId 2 is logo
          const logoImg = response.find(img => img.imageId === 2);
          if (logoImg) {
            setLogoImage(logoImg.imageUrl);
          }
        }
      } catch (error) {
        logger.error('Failed to load logo:', error);
        // Continue with default logo if backend fails
      }
    };

    loadLogo();
  }, []);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
    setSidebarMinimized(true);
  }, [sidebarHovered]);

  useEffect(() => {
    setProfileMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (profileMenuOpen && profileMenuAnchorRef.current && !document.body.contains(profileMenuAnchorRef.current)) {
      setProfileMenuOpen(false);
    }
  }, [profileMenuOpen]);
 
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Idle Timeout Handler */}
      <IdleTimeoutHandler />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#fff',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleSidebar}
            sx={{ mr: 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {logoImage ? (
              <img
                src={`${IMG_BASE_URL}${logoImage}`}
                alt="Company Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            ) : (
              <img
                src={defaultLogo}
                alt="Company Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            )}
          </Box>

          <NotificationBell />

          <IconButton
            edge="end"
            onClick={handleProfileMenuOpen}
            ref={profileMenuAnchorRef}
            color="inherit"
            sx={{
              ml: 1,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
              {user?.employeeName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>

          <Menu
            key={location.pathname}
            anchorEl={profileMenuAnchorRef.current}
            open={profileMenuOpen && !!profileMenuAnchorRef.current}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1
                }
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.employeeName || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.roleName || 'Role'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleChangePassword}>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        sx={{
          width: isSidebarExpanded ? DRAWER_WIDTH : DRAWER_WIDTH_MINIMIZED,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            width: isSidebarExpanded ? DRAWER_WIDTH : DRAWER_WIDTH_MINIMIZED,
            boxSizing: 'border-box',
            top: 64,
            height: 'calc(100% - 64px)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
            overflowY: 'auto',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fff',
            boxShadow: sidebarHovered ? '2px 0 8px rgba(0,0,0,0.1)' : 'none',
            zIndex: sidebarHovered ? 1201 : 'auto',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)'
              }
            }
          },
        }}
      >
        <Sidebar minimized={sidebarMinimized && !sidebarHovered} />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f4f6f9',
          transition: 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: `calc(100% - ${isSidebarExpanded ? DRAWER_WIDTH : DRAWER_WIDTH_MINIMIZED}px)`
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;

